import React from 'react';
import {
  Animated,
  Keyboard,
  PanResponder,
  PanResponderGestureState,
  Platform,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';

import ActivityIndicator from '@components/ActivityIndicator';
import Divider from '@components/Divider';
import { getPlaceSearchSelectionMapRegion } from '@components/Explorer/MapGeometry';
import {
  SheetSnapPoint,
  clampSheetOffset,
  getSheetReleaseSnapPoint,
  getSheetSnapOffset,
} from '@components/Explorer/MapSheetSnap';
import { MapMarkers } from '@components/Explorer/Markers';
import type { MapMarkerProfile } from '@components/Explorer/Markers';
import {
  DEFAULT_SEARCH_TARGETS,
  EMPTY_GROUPS,
  EMPTY_USERS,
} from '@components/Explorer/SearchDefaults';
import { SearchHeader } from '@components/Explorer/SearchHeader';
import {
  type ExplorerItem,
  getExplorerItems,
  getVisibleExplorerItems,
} from '@components/Explorer/explore';
import { filterUsers } from '@components/Explorer/search';
import type { SearchTargets, SelectedSearchFilter } from '@components/Explorer/search';
import { useExplorerMapRegionController } from '@components/Explorer/useExplorerMapRegionController';
import type { MapRegionChangeMeta } from '@components/Explorer/useExplorerMapRegionController';
import { useExplorerSearch } from '@components/Explorer/useExplorerSearch';
import MapView, { isMapAvailable } from '@components/Map';
import { PressableOpacity } from '@components/Pressable';
import Surface from '@components/Surface';
import { Caption } from '@components/Typography';
import { useAppTheme } from '@context/ThemeContext';
import { PlaceAutocompleteSelection } from '@db/Places';
import { getMapRegion } from '@helpers/Map';
import type { MapInitialRegion, MapRegion, MapViewport } from '@helpers/Map';
import type { Group, Plan, User } from '@types';

const MIN_COLLAPSED_SHEET_HEIGHT = 40;
const TARGET_COLLAPSED_SHEET_HEIGHT = 220;
const TARGET_MIN_MAP_HEIGHT = 240;
const SHEET_HEADER_GAP = 8;

type ExplorerMapSheetProps = {
  /** Loaded plans that can render as map markers and list rows. */
  plans: Plan[];
  /** Loaded groups used for group-scoped search results. */
  groups?: Group[];
  /** Loaded users that can render as residence markers. */
  users?: User[];
  /** Group ids that constrain group search results. Data loading is owned by Explorer. */
  searchGroupIds?: string[];
  /** User ids that constrain user search results. Data loading is owned by Explorer. */
  searchUserIds?: string[];
  /** Resolves the avatar/name shown for plan markers. */
  getPlanDisplayProfile: (plan: Plan) => MapMarkerProfile & { onPress?: () => void };
  /** Initial map center and optional viewport span. */
  initialRegion?: MapInitialRegion;
  /** Region pushed from outside the sheet, usually from selecting a place search result. */
  mapFocusRegion?: MapRegion;
  onPlanPress: (plan: Plan) => void;
  onUserPress?: (user: User) => void;
  header?: React.ReactNode;
  mapTestID?: string;
  mapLoading?: boolean;
  onMapRegionChange?: (region: MapRegion, meta: MapRegionChangeMeta) => void;
  onSearchFiltersChange?: (filters: SelectedSearchFilter[]) => void;
  renderContent: (items: ExplorerItem[]) => React.ReactNode;
  searchTargets?: SearchTargets;
};

export default function ExplorerMapSheet({
  plans,
  groups = EMPTY_GROUPS,
  users = EMPTY_USERS,
  searchGroupIds,
  searchUserIds,
  getPlanDisplayProfile,
  initialRegion,
  mapFocusRegion,
  onPlanPress,
  onUserPress,
  header,
  mapTestID,
  mapLoading = false,
  onMapRegionChange,
  onSearchFiltersChange,
  renderContent,
  searchTargets = DEFAULT_SEARCH_TARGETS,
}: ExplorerMapSheetProps) {
  const { height, width } = useWindowDimensions();
  const appTheme = useAppTheme();
  const [sheetSnapPoint, setSheetSnapPoint] = React.useState<SheetSnapPoint>('collapsed');
  const [containerViewport, setContainerViewport] = React.useState<MapViewport>();
  const [searchControlsHeight, setSearchControlsHeight] = React.useState<number>(0);
  const initialMapRegion = React.useMemo(() => getMapRegion(initialRegion), [initialRegion]);
  const {
    mapRegion,
    focusMapRegion,
    handleMapInteraction: handleMapRegionInteraction,
    handleMapRegionChangeComplete,
  } = useExplorerMapRegionController({
    initialMapRegion,
    mapFocusRegion,
    onMapRegionChange,
  });
  const search = useExplorerSearch({
    plans,
    groups,
    users,
    searchTargets,
    searchGroupIds,
    searchUserIds,
  });
  const availableHeight = containerViewport?.height || height;
  const markerViewport = React.useMemo<MapViewport>(
    () => ({
      height: Math.max(containerViewport?.height || height, 1),
      width: Math.max(containerViewport?.width || width, 1),
    }),
    [containerViewport?.height, containerViewport?.width, height, width]
  );
  const sheetAvailableHeight = Math.max(0, availableHeight);
  const measuredSheetTopOffset = searchControlsHeight + SHEET_HEADER_GAP;
  const expandedSheetHeight = Math.max(0, sheetAvailableHeight - measuredSheetTopOffset);
  const collapsedSheetMaxHeight = Math.max(
    0,
    sheetAvailableHeight - searchControlsHeight - TARGET_MIN_MAP_HEIGHT
  );
  const collapsedSheetMinHeight = Math.min(MIN_COLLAPSED_SHEET_HEIGHT, expandedSheetHeight);
  const collapsedSheetHeight = Math.min(
    TARGET_COLLAPSED_SHEET_HEIGHT,
    expandedSheetHeight,
    Math.max(collapsedSheetMinHeight, collapsedSheetMaxHeight)
  );
  const collapsedSheetOffset = Math.max(0, expandedSheetHeight - collapsedSheetHeight);
  const minimizedSheetOffset = Math.max(0, expandedSheetHeight - collapsedSheetMinHeight);
  const sheetSnapOffsets = React.useMemo(
    () => ({
      collapsed: collapsedSheetOffset,
      minimized: minimizedSheetOffset,
    }),
    [collapsedSheetOffset, minimizedSheetOffset]
  );
  const [sheetTranslateY] = React.useState(() => new Animated.Value(collapsedSheetOffset));
  const sheetDragStartOffset = React.useRef(collapsedSheetOffset);
  const sheetSnapPointRef = React.useRef<SheetSnapPoint>('collapsed');
  const sheetExpanded = sheetSnapPoint === 'expanded';
  const currentSheetHeight = Math.max(
    0,
    expandedSheetHeight - getSheetSnapOffset(sheetSnapPoint, sheetSnapOffsets)
  );
  const mapPadding = React.useMemo(
    () =>
      sheetExpanded
        ? undefined
        : {
            bottom: currentSheetHeight,
            left: 0,
            right: 0,
            top: measuredSheetTopOffset,
          },
    [currentSheetHeight, measuredSheetTopOffset, sheetExpanded]
  );
  const visibleUsers = React.useMemo(
    () => filterUsers(users, search.selectedFilters, search.displayMode),
    [users, search.selectedFilters, search.displayMode]
  );
  const candidateItems = React.useMemo(
    () =>
      getExplorerItems({
        plans: search.filteredPlans,
        users: visibleUsers,
        getPlanDisplayProfile,
      }),
    [search.filteredPlans, visibleUsers, getPlanDisplayProfile]
  );
  const visibleItems = React.useMemo(
    () => getVisibleExplorerItems({ items: candidateItems, region: mapRegion }),
    [candidateItems, mapRegion]
  );
  const listItems = visibleItems;

  const handleMapInteraction = React.useCallback(() => {
    Keyboard.dismiss();
    handleMapRegionInteraction();
  }, [handleMapRegionInteraction]);

  const handlePlaceSearchSelect = React.useCallback(
    (selection: PlaceAutocompleteSelection) => {
      focusMapRegion(getPlaceSearchSelectionMapRegion(selection));
    },
    [focusMapRegion]
  );

  React.useEffect(() => {
    onSearchFiltersChange?.(search.selectedFilters);
  }, [onSearchFiltersChange, search.selectedFilters]);

  const snapSheet = React.useCallback(
    (snapPoint: SheetSnapPoint, animated = true) => {
      const targetOffset = getSheetSnapOffset(snapPoint, sheetSnapOffsets);
      sheetSnapPointRef.current = snapPoint;
      setSheetSnapPoint(snapPoint);
      sheetDragStartOffset.current = targetOffset;
      if (animated) {
        sheetTranslateY.stopAnimation();
        Animated.spring(sheetTranslateY, {
          toValue: targetOffset,
          useNativeDriver: Platform.OS !== 'web',
          damping: 24,
          stiffness: 260,
        }).start();
      } else {
        sheetTranslateY.setValue(targetOffset);
      }
    },
    [sheetSnapOffsets, sheetTranslateY]
  );

  React.useEffect(() => {
    const targetOffset = getSheetSnapOffset(sheetSnapPointRef.current, sheetSnapOffsets);
    sheetDragStartOffset.current = targetOffset;
    sheetTranslateY.stopAnimation();
    sheetTranslateY.setValue(targetOffset);
  }, [sheetSnapOffsets, sheetTranslateY]);

  const handleSheetRelease = React.useCallback(
    (_: unknown, gestureState: PanResponderGestureState) => {
      const currentOffset = sheetDragStartOffset.current + gestureState.dy;
      snapSheet(
        getSheetReleaseSnapPoint({
          currentOffset,
          velocityY: gestureState.vy,
          offsets: sheetSnapOffsets,
        })
      );
    },
    [sheetSnapOffsets, snapSheet]
  );

  const shouldSetSheetPanResponder = React.useCallback(
    (_: unknown, gestureState: PanResponderGestureState) => Math.abs(gestureState.dy) > 8,
    []
  );

  const handleSheetGrant = React.useCallback(() => {
    sheetTranslateY.stopAnimation((currentOffset) => {
      sheetDragStartOffset.current = clampSheetOffset(currentOffset, minimizedSheetOffset);
    });
  }, [minimizedSheetOffset, sheetTranslateY]);

  const handleSheetMove = React.useCallback(
    (_: unknown, gestureState: PanResponderGestureState) => {
      const nextOffset = clampSheetOffset(
        sheetDragStartOffset.current + gestureState.dy,
        minimizedSheetOffset
      );
      sheetTranslateY.setValue(nextOffset);
    },
    [minimizedSheetOffset, sheetTranslateY]
  );

  const sheetPanResponder = React.useMemo(
    () =>
      // PanResponder stores these callbacks for responder events; it does not call them during render.
      // eslint-disable-next-line react-hooks/refs
      PanResponder.create({
        onMoveShouldSetPanResponder: shouldSetSheetPanResponder,
        onPanResponderGrant: handleSheetGrant,
        onPanResponderMove: handleSheetMove,
        onPanResponderRelease: handleSheetRelease,
        onPanResponderTerminate: handleSheetRelease,
      }),
    [handleSheetGrant, handleSheetMove, handleSheetRelease, shouldSetSheetPanResponder]
  );

  const searchHeader = (
    <SearchHeader
      header={header}
      searchText={search.searchText}
      selectedFilters={search.selectedFilters}
      displayMode={search.displayMode}
      dateRange={search.dateRange}
      usersLoading={search.usersLoading}
      groupsLoading={search.groupsLoading}
      placesLoading={search.placesLoading}
      placeSelectionLoading={search.placeSelectionLoading}
      searchTargets={searchTargets}
      visibleUserResults={search.visibleUserResults}
      visibleGroupResults={search.visibleGroupResults}
      placeResults={search.placeResults}
      setSearchText={search.setSearchText}
      setSelectedFilters={search.setSelectedFilters}
      setDisplayMode={search.setDisplayMode}
      setDateRange={search.setDateRange}
      onPlaceSelect={handlePlaceSearchSelect}
      onControlsLayout={setSearchControlsHeight}
      selectFilter={search.selectFilter}
    />
  );

  const MapContent = isMapAvailable() ? (
    <MapView
      initialRegion={initialMapRegion}
      onPanDrag={handleMapInteraction}
      onRegionChangeComplete={handleMapRegionChangeComplete}
      onTouchStart={handleMapInteraction}
      // TODO: add padding on Android when this issue is fixed
      // https://github.com/react-native-maps/react-native-maps/issues/5837
      mapPadding={Platform.select({ default: mapPadding, android: undefined })}
      region={mapRegion}
      style={styles.map}
      showsUserLocation
      testID={mapTestID}>
      <MapMarkers
        items={visibleItems}
        mapRegion={mapRegion}
        mapViewport={markerViewport}
        setMapRegion={focusMapRegion}
        onPlanPress={onPlanPress}
        onUserPress={onUserPress}
        showDetails
      />
    </MapView>
  ) : (
    <View style={[styles.mapUnavailable, { backgroundColor: appTheme.colors.background }]} />
  );

  return (
    <View
      style={styles.container}
      testID="components.Explorer.MapSheet.Container"
      onLayout={(event) => {
        const nextViewport = event.nativeEvent.layout;
        setContainerViewport((currentViewport) =>
          currentViewport?.height === nextViewport.height &&
          currentViewport.width === nextViewport.width
            ? currentViewport
            : { height: nextViewport.height, width: nextViewport.width }
        );
      }}>
      <View style={styles.mapContainer}>
        {MapContent}
        {mapLoading ? (
          <Surface
            style={[
              styles.mapLoading,
              {
                backgroundColor: appTheme.colors.card,
                pointerEvents: 'none',
                top: searchControlsHeight + 8,
              },
            ]}>
            <ActivityIndicator size="small" />
            <Caption style={styles.mapLoadingText}>Updating</Caption>
          </Surface>
        ) : null}
      </View>
      <View style={styles.headerOverlay}>{searchHeader}</View>
      <Animated.View
        testID="components.Explorer.MapSheet.Sheet"
        style={[
          styles.sheet,
          {
            backgroundColor: appTheme.colors.card,
            borderTopColor: appTheme.colors.border,
            height: expandedSheetHeight,
            transform: [{ translateY: sheetTranslateY }],
          },
        ]}>
        <View
          style={[styles.sheetViewport, { height: currentSheetHeight }]}
          testID="components.Explorer.MapSheet.Viewport">
          <View
            style={styles.sheetHandleContainer}
            testID="components.Explorer.MapSheet.HandleContainer"
            {...sheetPanResponder.panHandlers}>
            <PressableOpacity
              accessibilityLabel={sheetExpanded ? 'Collapse plans sheet' : 'Expand plans sheet'}
              accessibilityRole="button"
              accessibilityState={{ expanded: sheetExpanded }}
              onPress={() => snapSheet(sheetExpanded ? 'collapsed' : 'expanded')}
              pressedOpacity={1}
              style={styles.sheetHandleButton}
              testID="components.Explorer.MapSheet.Handle">
              <View
                style={[styles.sheetHandle, { backgroundColor: appTheme.colors.placeholder }]}
              />
            </PressableOpacity>
          </View>
          <Divider />
          <View style={styles.sheetContent}>{renderContent(listItems)}</View>
        </View>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'stretch',
    flex: 1,
    overflow: 'hidden',
    width: '100%',
  },
  headerOverlay: {
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 2,
  },
  map: {
    flex: 1,
  },
  mapContainer: {
    ...StyleSheet.absoluteFill,
  },
  mapLoading: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    minHeight: 36,
    paddingHorizontal: 12,
    position: 'absolute',
    right: 12,
    zIndex: 3,
  },
  mapLoadingText: {
    marginVertical: 0,
  },
  mapUnavailable: {
    flex: 1,
  },
  sheet: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderTopWidth: StyleSheet.hairlineWidth,
    bottom: 0,
    elevation: 8,
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    right: 0,
    ...Platform.select({
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.16,
        shadowRadius: 8,
      },
      web: {
        boxShadow: '0px -2px 8px rgba(0, 0, 0, 0.16)',
      },
    }),
    zIndex: 1,
  },
  sheetContent: {
    flex: 1,
    marginBottom: 5,
    width: '100%',
  },
  sheetHandle: {
    borderRadius: 2,
    height: 4,
    width: 38,
  },
  sheetHandleButton: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 24,
    paddingHorizontal: 24,
    paddingVertical: 8,
  },
  sheetHandleContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: MIN_COLLAPSED_SHEET_HEIGHT,
    paddingVertical: 5,
  },
  sheetViewport: {
    overflow: 'hidden',
    width: '100%',
  },
});
